import { useState } from "react";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daikq63oh653ivfvoff0";

interface Status {
  type: "idle" | "submitting" | "success" | "error";
  message: string;
}

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>({ type: "idle", message: "" });
  const [chars, setChars] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);

    // 反垃圾校验：蜜罐字段被填写则静默处理
    const guard = String(payload.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus({ type: "success", message: "线索已提交，我们会尽快完成初筛并反馈。" });
      formEl.reset();
      setChars(0);
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = typeof value === "string" ? value.trim() : "";
      if (str) body.append(key, str);
    });

    setStatus({ type: "submitting", message: "" });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus({ type: "success", message: "线索已提交，我们会尽快完成初筛并反馈。感谢你的信任。" });
        formEl.reset();
        setChars(0);
      } else {
        setStatus({
          type: "error",
          message: serverMsg || "提交失败，请稍后重试，或直接发送邮件到 tips@kanliaobaoliao.com。",
        });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，提交没有成功。请稍后重试，或发送邮件到 tips@kanliaobaoliao.com。" });
    }
  };

  const fieldClass =
    "w-full rounded-md border border-foreground-950/15 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form
      data-readdy-form
      id="kanliao-submit-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-foreground-950/10 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label htmlFor="title" className="mb-2 block text-sm font-medium text-foreground-800">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input id="title" name="title" type="text" required placeholder="用一句话概括这条线索" className={fieldClass} />
        </div>

        <div>
          <label htmlFor="category" className="mb-2 block text-sm font-medium text-foreground-800">
            线索分类
          </label>
          <select id="category" name="category" defaultValue="" className={fieldClass}>
            <option value="">请选择分类</option>
            {categories.map((cat) => (
              <option key={cat.key} value={cat.label}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="location" className="mb-2 block text-sm font-medium text-foreground-800">
            涉及地区
          </label>
          <input id="location" name="location" type="text" placeholder="如：华东地区 / 多个城市" className={fieldClass} />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="description" className="mb-2 block text-sm font-medium text-foreground-800">
            线索描述 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            required
            maxLength={500}
            rows={6}
            onChange={(e) => setChars(e.target.value.length)}
            placeholder="请描述事情经过、时间节点与关键细节，尽量写清哪些内容你能够佐证。"
            className={`${fieldClass} resize-none`}
          />
          <p className="mt-1.5 text-right text-xs text-foreground-500">{chars}/500</p>
        </div>

        <div className="md:col-span-2">
          <label htmlFor="materials" className="mb-2 block text-sm font-medium text-foreground-800">
            已掌握的材料
          </label>
          <input
            id="materials"
            name="materials"
            type="text"
            placeholder="如：聊天记录截图、合同照片、现场录音（如需补充，可在邮件中说明）"
            className={fieldClass}
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input id="email" name="email" type="email" placeholder="可填一次性邮箱，用于接收核实反馈" className={fieldClass} />
        </div>

        <div>
          <label htmlFor="contact" className="mb-2 block text-sm font-medium text-foreground-800">
            其他联系方式
          </label>
          <input id="contact" name="contact" type="text" placeholder="选填，如加密通信方式" className={fieldClass} />
        </div>
      </div>

      {/* 反垃圾字段：普通文本输入，不使用 inline style / hidden / sr-only */}
      <div className="form-field-guard" aria-hidden="true">
        <label htmlFor="website_alt">公司网站</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们在核实过程中使用你提供的材料。你可以选择匿名，我们不会强制要求真实身份。
        </p>
        <button
          type="submit"
          disabled={status.type === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status.type === "submitting" ? "ri-loader-4-line animate-spin text-base" : "ri-mail-send-line text-base"} aria-hidden="true"></i>
          {status.type === "submitting" ? "正在提交…" : "提交线索"}
        </button>
      </div>

      {status.type === "success" && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3" role="status">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600" aria-hidden="true"></i>
          <p className="text-sm text-secondary-900">{status.message}</p>
        </div>
      )}
      {status.type === "error" && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3" role="alert">
          <i className="ri-error-warning-line mt-0.5 text-lg text-primary-600" aria-hidden="true"></i>
          <p className="text-sm text-primary-900">{status.message}</p>
        </div>
      )}
    </form>
  );
}